<template>
    <div>
        <!-- Loading Spinner -->
        <div v-if="loading" class="ppv2-flex ppv2-items-center ppv2-justify-center ppv2-py-20">
            <div class="ppv2-text-center">
                <div
                    class="ppv2-animate-spin ppv2-rounded-full ppv2-h-12 ppv2-w-12 ppv2-border-b-4 ppv2-border-purple-600 ppv2-mx-auto"
                ></div>
                <span class="ppv2-mt-4 ppv2-block ppv2-text-base ppv2-text-gray-500 poppinsMedium">Chargement des devis...</span>
            </div>
        </div>

        <!-- DataTable -->
        <data-table
            v-else
            ref="dataTable"
            :items="quotes"
            :columns="tableColumns"
            :currentPage="meta.current_page"
            :totalPages="meta.last_page"
            :perPage="perPage"
            :total="meta.total"
            :enableMultiSelect="true"
            :enableDragDrop="false"
            :selectedItems="selectedQuotes"
            @page-changed="handlePageChange"
            @item-selected="handleItemSelected"
            @select-all-toggled="handleSelectAllToggled"
            @row-click="handleRowClick"
            :row-class="getRowClass"
            @selection-changed="$emit('selection-changed', selectedQuotes.length)"
            class="ppv2-min-w-full"
        >
            <!-- Numéro Column -->
            <template #column-fiscal_number="{ item }">
                <a
                    v-if="item.resource === 'invoice'"
                    :href="`/back-office/commandes/${item.order_id}/descriptif`"
                    class="ppv2-underline ppv2-text-blue-600 hover:ppv2-text-blue-800"
                >
                    {{ item.order_id ? 'Devis #' + item.order_id : '-' }}
                </a>
                <span v-else class="ppv2-text-gray-500">-</span>
            </template>

            <!-- Client Column -->
            <template #column-customer="{ item }">
                <a
                    v-if="item.user?.id"
                    href="#"
                    @click.prevent="openCustomerModal(item.user.id)"
                    class="ppv2-text-black ppv2-transition-colors ppv2-text-sm ppv2-underline hover:ppv2-text-blue-600"
                >
                    {{ item.user.first_name }} {{ item.user.last_name }}
                </a>
                <span v-else-if="item.user?.first_name" class="ppv2-text-black ppv2-text-sm">
                    {{ item.user.first_name }} {{ item.user.last_name }}
                </span>
                <span v-else class="ppv2-text-xs ppv2-italic">Client supprimé</span>
            </template>

            <!-- Statut Column -->
            <template #column-status="{ item }">
                <span
                    :class="[
                        getStatusBadgeClass(item),
                        'ppv2-inline-flex ppv2-px-3 ppv2-py-1 ppv2-rounded-md ppv2-tracking-wide',
                    ]"
                >
                    {{ getStatusLabel(item) }}
                </span>
            </template>

            <!-- Date de création Column -->
            <template #column-created_at="{ item }">
                <div class="ppv2-flex ppv2-flex-col">
                    {{ formatDateShort(item.created_at) }}
                </div>
            </template>

            <!-- Émission Column -->
            <template #column-emission="{ item }">
                <div class="ppv2-flex ppv2-flex-col">
                    {{ formatDateShort(item.created_at) }}
                </div>
            </template>

            <!-- Échéance Column -->
            <template #column-due_date="{ item }">
                <span v-if="item.resource === 'answer' || !item.due_date" class="ppv2-text-gray-500">-</span>
                <span
                    v-else
                    :class="{
                        'ppv2-text-red-500': isOverdue(item),
                        'ppv2-text-black': !isOverdue(item),
                    }"
                >
                    {{ formatDateShort(item.due_date) }}
                </span>
            </template>

            <!-- Restant HT Column (Not always relevant for Quote but keeping structure) -->
            <!-- <template #column-remaining="{ item }">
                {{ formatCurrency(item.remaining_amount || 0) }}
            </template> -->

            <!-- Montant HT Column -->
            <template #column-total="{ item }">
                <span v-if="item.resource === 'answer'" class="ppv2-text-gray-500">-</span>
                <div v-else class="ppv2-flex ppv2-flex-col">
                    <span v-if="item.promotion && Number(item.discount_total) > 0 && item.total_net != null">
                        <s class="ppv2-text-gray-400">{{ formatCurrency(item.total_price || 0) }}</s>
                        {{ formatCurrency(item.total_net) }}
                    </span>
                    <span v-else>{{ formatCurrency(item.total_price || 0) }}</span>
                    <!-- Aperçu de la remise (caisse nomade) : le code SÉLECTIONNÉ, posé à la conversion. -->
                    <span
                        v-if="item.promotion && Number(item.discount_total) > 0"
                        class="ppv2-inline-flex ppv2-items-center ppv2-self-start ppv2-gap-1 ppv2-px-2 ppv2-py-0.5 ppv2-rounded-full ppv2-text-[10px] poppinsMedium ppv2-mt-0.5"
                        style="
                            background-color: rgba(74, 222, 128, 0.12);
                            border: 1px solid rgba(74, 222, 128, 0.35);
                            color: #4ade80;
                        "
                    >
                        <Tag :size="11" /> {{ promoCodeDisplay(item) }}
                        <span class="ppv2-opacity-80">· −{{ formatCurrency(item.discount_total) }}</span>
                    </span>
                </div>
            </template>

            <!-- Resource Column -->
            <template #column-resource="{ item }">
                <span
                    v-if="item.resource === 'answer'"
                    class="ppv2-inline-flex ppv2-px-2 ppv2-py-1 ppv2-rounded-md ppv2-bg-blue-100 ppv2-text-blue-800 ppv2-text-xs poppinsMedium"
                    >{{ item.type_label }}</span
                >
                <span
                    v-else-if="item.resource === 'invoice'"
                    class="ppv2-inline-flex ppv2-px-2 ppv2-py-1 ppv2-rounded-md ppv2-bg-purple-100 ppv2-text-purple-800 ppv2-text-xs poppinsMedium"
                    >{{ item.type_label }}</span
                >
                <span v-else class="ppv2-text-gray-500">-</span>
            </template>

            <!-- Actions Column -->
            <!--
                Les actions dépendent du statut métier de la ligne :
                - Expiré / Gagné / Perdu / Demande : consultation seule (oeil).
                - Brouillon : envoi possible UNIQUEMENT si un compte client est rattaché
                  (un devis en mode "client passager" n'a pas de destinataire email).
                - Envoyé : le client a reçu le devis, il peut donc être marqué gagné.
                Rappel : ce n'est qu'un affichage — le serveur reste seul juge
                (permissions "Modifier un devis" + contrôles dans DevisController).
            -->
            <template #column-actions="{ item }">
                <div class="ppv2-flex ppv2-justify-end ppv2-items-center ppv2-gap-2">
                    <button
                        v-if="canValidate(item)"
                        @click="validateQuote(item)"
                        class="ppv2-w-8 ppv2-h-8 ppv2-rounded-lg ppv2-flex ppv2-items-center ppv2-justify-center ppv2-text-green-600 hover:ppv2-text-white hover:ppv2-bg-green-600 ppv2-transition-colors"
                        :title="$t('quotes.validate_quote') || 'Valider devis'"
                        :disabled="validatingId === item.order_id"
                    >
                        <Check :size="18" />
                    </button>
                    <button
                        v-if="canSend(item)"
                        @click="sendQuote(item)"
                        class="ppv2-w-8 ppv2-h-8 ppv2-rounded-lg ppv2-flex ppv2-items-center ppv2-justify-center ppv2-text-[#7D2DBD] hover:ppv2-text-white hover:ppv2-bg-[#7D2DBD] ppv2-transition-colors"
                        :title="$t('quotes.card.actions.send')"
                        :disabled="sendingId === item.order_id"
                    >
                        <Send :size="18" />
                    </button>
                    <button
                        @click="viewQuote(item)"
                        class="ppv2-w-8 ppv2-h-8 ppv2-rounded-lg ppv2-flex ppv2-items-center ppv2-justify-center ppv2-text-gray-400 hover:ppv2-text-[#7D2DBD] hover:ppv2-bg-[#7D2DBD]/10 ppv2-transition-colors"
                        :title="$t('quotes.card.view_edit')"
                    >
                        <Eye :size="18" />
                    </button>
                </div>
            </template>

            <!-- Bulk Actions Slot Passthrough -->
            <template #bulk-actions="slotProps">
                <slot name="bulk-actions" v-bind="slotProps"></slot>
            </template>
        </data-table>

        <ExperienceDatesModal
            :show="showExpDatesModal"
            :experiences="undatedExperiences"
            :subtitle="expModalSubtitle"
            :order-id="Number(expModalQuote && expModalQuote.order_id) || null"
            @close="showExpDatesModal = false"
            @confirm="retryPendingValidation"
        />
    </div>
</template>

<script>
import DataTable from '../Shared/DataTable.vue'
import { eventBus } from '../Customer/eventBus'
import { MoreHorizontal, Eye, Download, Check, Send, Tag } from 'lucide-vue'
import { getPerPage, setPerPage } from '../../utils/perPage'
import ExperienceDatesModal from './ExperienceDatesModal.vue'

export default {
    name: 'DevisDataTable',
    components: {
        DataTable,
        MoreHorizontal,
        Eye,
        Download,
        Check,
        Send,
        Tag,
        ExperienceDatesModal,
    },
    props: {
        startDate: {
            type: String,
            required: true,
        },
        endDate: {
            type: String,
            required: true,
        },
        typeFilter: {
            type: String,
            default: 'all',
        },
        searchQuery: {
            type: String,
            default: '',
        },
        filters: {
            // NEW: Advanced filters
            type: Object,
            default: () => ({}),
        },
        columns: {
            type: Array,
            default: () => [
                { key: 'status', label: 'Statut', sortable: true, type: 'badge' },
                { key: 'resource', label: 'Type', sortable: true, type: 'badge' },
                { key: 'created_at', label: 'Date de création', sortable: true, type: 'date' },
                { key: 'emission', label: 'Émission', sortable: true, type: 'date' },
                { key: 'due_date', label: 'Échéance', sortable: true, type: 'date' },
                { key: 'customer', label: 'Client', sortable: true, type: 'text' },
                { key: 'fiscal_number', label: 'Numéro', sortable: true, type: 'text' },
                // { key: 'remaining', label: 'Restant HT', sortable: true },
                { key: 'total', label: 'Montant HT', sortable: true, type: 'number' },
            ],
        },
    },
    data() {
        return {
            quotes: [], // RENAMED from invoices
            loading: false,
            selectedQuotes: [], // RENAMED from selectedInvoices
            meta: {
                current_page: 1,
                last_page: 1,
                total: 0,
            },
            perPage: getPerPage('devis_items_per_page', 25),
            localSearchQuery: '',
            validatingId: null,
            sendingId: null,
            // Modale « expériences sans date » (garde serveur QuoteExperienceDateGuard).
            showExpDatesModal: false,
            undatedExperiences: [],
            expModalQuote: null,
        }
    },
    computed: {
        tableColumns() {
            // Always show actions column
            return [...this.columns, { key: 'actions', label: '', sortable: false }]
        },
        expModalSubtitle() {
            const q = this.expModalQuote
            if (!q) return ''
            const ref = q.quote_serial_number || q.order_id || 'N/A'
            const name = q.customer_name || q.client_name || q.customer || ''
            return name ? `#${ref} · ${name}` : `#${ref}`
        },
    },
    watch: {
        startDate() {
            this.meta.current_page = 1
            this.fetchDevis()
        },
        endDate() {
            this.meta.current_page = 1
            this.fetchDevis()
        },
        typeFilter() {
            this.meta.current_page = 1
            this.fetchDevis()
        },
    },
    mounted() {
        this.fetchDevis()
        eventBus.$on('dt-selected-perpage-changed', this.handlePerPageChange)
    },
    beforeDestroy() {
        eventBus.$off('dt-selected-perpage-changed', this.handlePerPageChange)
    },
    methods: {
        // Une « remise instantanée » (is_special) n'affiche jamais son code généré — seulement le
        // titre (règle de la caisse). Un code normal affiche son code.
        promoCodeDisplay(item) {
            const p = item.promotion
            if (!p) return ''
            return p.is_special ? p.title : p.code
        },
        async fetchDevis() {
            this.loading = true
            try {
                // Endpoint to be created in DevisController
                const response = await this.axios.get('/back-office/devis-v3/list', {
                    params: {
                        startDate: this.startDate,
                        endDate: this.endDate,
                        search: this.localSearchQuery || this.searchQuery,
                        typeFilter: this.typeFilter,
                        page: this.meta.current_page,
                        perPage: this.perPage,
                        ...this.filters, // Pass advanced filters
                    },
                })

                const data = response.data || response
                this.quotes = data.data || []
                this.meta.current_page = parseInt(data.meta?.current_page) || 1
                this.meta.last_page = parseInt(data.meta?.last_page) || 1
                this.meta.total = parseInt(data.meta?.total) || 0
            } catch (error) {
                this.$toast.error(error.response.data.message)
                this.quotes = []
            } finally {
                this.loading = false
            }
        },
        handlePageChange(page) {
            this.meta.current_page = page
            this.fetchDevis()
        },
        handlePerPageChange(newPerPage) {
            this.perPage = parseInt(newPerPage)
            setPerPage('devis_items_per_page', this.perPage)
            this.meta.current_page = 1
            this.fetchDevis()
        },
        handleSearchUpdate(searchValue) {
            this.localSearchQuery = searchValue
            this.meta.current_page = 1
            this.fetchDevis()
        },
        handleItemSelected(itemId) {
            const index = this.selectedQuotes.indexOf(itemId)
            if (index > -1) {
                this.selectedQuotes.splice(index, 1)
            } else {
                this.selectedQuotes.push(itemId)
            }
            this.$emit('selection-changed', this.selectedQuotes.length)
        },
        handleSelectAllToggled() {
            if (this.selectedQuotes.length === this.quotes.length) {
                this.selectedQuotes = []
            } else {
                this.selectedQuotes = this.quotes.map((q) => q.id)
            }
            this.$emit('selection-changed', this.selectedQuotes.length)
        },
        reload() {
            this.meta.current_page = 1
            this.fetchDevis()
        },
        formatCurrency(amount) {
            return new Intl.NumberFormat(this.$currencyLocale?.replace('_', '-') || 'fr-FR', {
                style: 'currency',
                currency: this.$codeCurrency,
            }).format(amount || 0)
        },
        formatDateShort(dateString) {
            if (!dateString) return '-'
            return new Date(dateString).toLocaleDateString('fr-FR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
            })
        },
        getStatusBadgeClass(item) {
            console.log('getStatusBadgeClass', item)
            // Logic for Devis statuses badges
            const statusName = item.status_label || ''

            // Devis status values from DevisInvoice model: Brouillon, Demande, Envoyé, Expiré, Gagné, Perdu
            if (statusName === 'Gagné') return 'ppv2-bg-[#035B2F] ppv2-text-white'
            if (statusName === 'Envoyé') return 'ppv2-bg-[#9333EA] ppv2-text-white'
            if (statusName === 'Expiré') return 'ppv2-bg-[#DBA800] ppv2-text-white'
            if (statusName === 'Perdu') return 'ppv2-bg-[#F93333] ppv2-text-white'
            if (statusName === 'Demande') return 'ppv2-bg-gray-400 ppv2-text-white'
            if (statusName === 'Brouillon') return 'ppv2-bg-gray-100 ppv2-text-gray-600'

            return 'ppv2-bg-gray-100 ppv2-text-gray-600'
        },
        getStatusLabel(item) {
            const statusName = item.status_label || 'Demande'

            // Map status labels to i18n keys
            const statusMap = {
                Demande: this.$t('devis.status.request'),
                Envoyé: this.$t('devis.status.sent'),
                Gagné: this.$t('devis.status.signed'),
                'Entièrement payé': this.$t('devis.status.fully_paid'),
                Perdu: this.$t('devis.status.lost'),
                Brouillon: this.$t('devis.status.draft'),
                Expiré: this.$t('devis.status.expired'),
            }

            return statusMap[statusName] || statusName
        },
        handleRowClick(item) {
            if (item.resource === 'answer') {
                this.$emit('open-answer-detail', item.id)
            }
        },
        getRowClass(item) {
            return item.resource === 'answer' ? 'ppv2-cursor-pointer' : ''
        },
        openCustomerModal(userId) {
            if (typeof window.setOrderToModal === 'function') {
                window.setOrderToModal(userId)
            } else {
                window.open(`/back-office/v3/customer/${userId}/edit`, '_blank')
            }
        },
        // Un devis "client passager" n'a aucun compte client rattaché : pas de
        // destinataire possible pour l'email, donc pas d'envoi (le backend
        // sentQuote() rejette de toute façon un order sans user->email).
        isPassenger(item) {
            return !item.user?.id
        },
        // Envoi possible uniquement sur un Brouillon rattaché à un client.
        canSend(item) {
            return item.resource === 'invoice' && item.status_label === 'Brouillon' && !this.isPassenger(item)
        },
        // "Marquer gagné" n'a de sens qu'une fois le devis envoyé au client :
        // ni sur un Brouillon (jamais reçu), ni sur un Expiré / Gagné / Perdu.
        canValidate(item) {
            return item.resource === 'invoice' && item.status_label === 'Envoyé'
        },
        async sendQuote(item) {
            if (this.sendingId || !item.order_id) return
            this.sendingId = item.order_id
            try {
                await this.axios.get(`/back-office/devis/${item.order_id}/sent-quote`)
                this.$toast.success(this.$t('quotes.card.toasts.sent'))
                this.fetchDevis()
            } catch (error) {
                this.$toast.error(error.response?.data?.message || this.$t('quotes.card.toasts.error'))
            } finally {
                this.sendingId = null
            }
        },
        async validateQuote(item) {
            this.validatingId = item.order_id
            try {
                await this.axios.post(`/back-office/devis/${item.order_id}/validate-api`)
                this.fetchDevis()
            } catch (error) {
                // Expérience à créneau sans date (400) : on ouvre la modale de saisie des dates.
                const undated = error.response?.data?.errors?.undated_experiences || error.response?.data?.undated_experiences
                if ([400, 422].includes(error.response?.status) && Array.isArray(undated) && undated.length) {
                    this.openExpDatesModal(undated, item)
                } else {
                    this.$toast.error(error.response?.data?.message || this.$t('quotes.card.toasts.error'))
                }
            } finally {
                this.validatingId = null
            }
        },
        openExpDatesModal(list, item) {
            this.expModalQuote = item
            // Champs enrichis conservés (item_id, slug, ticket_selections…) : la modale rouvre
            // le sélecteur de créneau sur place, sans recharger la ligne.
            // On PRÉSERVE la date renvoyée par le serveur : les lignes déjà datées s'affichent
            // en vert (lecture seule), seules les non datées (date null) portent l'action.
            this.undatedExperiences = list.map((e) => ({ ...e, date: e.date ?? null }))
            this.showExpDatesModal = true
        },
        // Toutes les lignes datées sur place : on relance la validation « Gagné » initialement
        // bloquée par le garde (aucune navigation), qui doit désormais passer.
        retryPendingValidation() {
            this.showExpDatesModal = false
            if (this.expModalQuote) {
                this.validateQuote(this.expModalQuote)
            }
        },
        // Un devis encore vivant (Brouillon / Envoyé / Expiré) se rouvre dans l'écran
        // de rédaction V3 pour être modifié ou relancé. Un devis clos (Gagné / Perdu)
        // n'est plus modifiable : on renvoie sur le descriptif de commande.
        isEditableQuote(item) {
            return item.resource === 'invoice' && ['Brouillon', 'Envoyé', 'Expiré'].includes(item.status_label)
        },
        viewQuote(item) {
            if (item.resource === 'answer') {
                this.$emit('open-answer-detail', item.id)
                return
            }
            if (!item.order_id) return
            // Brouillon / Envoyé / Expiré : éditeur pleinement éditable.
            // Gagné (accepté/signé) : même éditeur, mais ouvert en mode verrouillé
            // (Create.vue s'appuie sur quote_accepted_at pour désactiver l'édition,
            // sauf la case Option). Perdu : consultation seule (descriptif).
            if (this.isEditableQuote(item) || item.status_label === 'Gagné') {
                window.location.href = `/back-office/v3/devis/create/${item.order_id}`
                return
            }
            window.location.href = `/back-office/commandes/${item.order_id}/descriptif`
        },
        downloadQuote(item) {
            // window.open(`/back-office/devis/${item.id}/download`, '_blank')
        },
        isOverdue(item) {
            if (!item.due_date) return false
            return new Date(item.due_date) < new Date()
        },
    },
}
</script>
